import equityIcon from '../../assets/storymap/demographics.svg';
import livabilityIcon from '../../assets/storymap/livability.svg';
import economyIcon from '../../assets/storymap/economy.svg';
import accessibilityIcon from '../../assets/storymap/accessibility.svg';
import environmentIcon from '../../assets/storymap/environment.svg';
import portLogo from "../../assets/storymap/Port_of_Seattle_Logo.svg";
import caiLogo from "../../assets/CAI Full Color.png";
import { useNavigate } from 'react-router-dom';
import { handleCloseStorymap, handleRestoreDefaultView, toggleOverlayLayer } from '../../utils/messages';
import { partners } from './utils/partners';

const indicators = [
  { icon: equityIcon, label: "Equity", description: "index is used to understand our impact on specific aspects of quality of life: Economy, Livability, Accessibility, and Environment", id: 347 },
  { icon: accessibilityIcon, label: "Accessibility", description: "focuses on resources and services that enable full participation in public life, such as parks, healthy food, infrastructure, and civic engagement.", id: 350 },
  { icon: livabilityIcon, label: "Livability", description: "highlights criteria for a safe and healthy standard of living, including data reflecting health, living conditions, and housing", id: 345 },
  { icon: economyIcon, label: "Economy", description: "subindex represents opportunities for wealth creation and retention, encompassing factors such as income, employment, and education.", id: 351 },
  { icon: environmentIcon, label: "Environmental", description: "subindex captures environmental health and impacts on community vitality, including pollution, tree cover, and traffic density.", id: 353 },
];

export function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8l4 4-4 4" />
      <path d="M8 12h8" />
    </svg>
  );
}

// Intro Banner that should cover the full screen width (this is accomplished by sending a message to the parent app to occupy the entire width of it's container)
const Intro = () => {
    const navigate = useNavigate();

    return (
    <section className="relative w-full min-h-screen helvetica-page">
        {/* Frosted glass background */}
        <div className="absolute inset-0 -z-10 bg-white/10 backdrop-blur-sm" />
        {/* Banner / Header Area */}
        <div className="relative w-full bg-[#4F92B1]/60 px-6 pt-10 pb-6 md:px-12 lg:px-20">
            <h1 className="text-center text-3xl font-extrabold uppercase tracking-wider text-white md:text-4xl lg:text-5xl">
            Equity at the Port of Seattle
            </h1>

            <div className="mx-auto mt-6 max-w-6xl space-y-4 mb-6">
                <p className="text-base leading-relaxed text-white md:text-lg">
                    As one of the largest economic drivers in the Pacific Northwest
                    region, the Port of Seattle strives for our programs and initiatives
                    to mirror the diversity of our community, instill principles of
                    equity throughout our culture, and ensure a fair and intentional
                    distribution of opportunities for all.
                </p>
                <p className="text-base leading-relaxed text-white md:text-lg">
                    In 2019, the Port of Seattle became the first port authority in the
                    country to establish an office of equity. In doing so, the Port
                    commits time and resources to embed equity into the fabric of our
                    work. We strive to be a leading organization, both regionally and
                    nationally, in promoting programs and policies that help grow the
                    economy and create opportunities for everyone.
                </p>
                <p className="text-base leading-relaxed text-white md:text-lg">
                    Here are two ways to explore the Port's equity work.
                </p>
            </div>
        </div>

        {/* Cards Section */}
        <div className="relative z-10 bg-transparent mx-auto -mt-8 grid max-w-7xl gap-6 px-6 pb-12 md:grid-cols-2 md:px-12 lg:px-8">
            {/* Equity Index Card */}
            <div className="rounded-[3rem_0rem_3rem_0rem] bg-[#fbfbfb] p-8 shadow-lg">
                <div className="flex items-start border-b-2 border-black pb-4 mb-4">
                    <h2 className="text-2xl font-bold md:text-3xl">
                        Equity Index
                    </h2>
                </div>

                {/* Explore the Map Button */}
                <button
                    type="button"
                    onClick={handleCloseStorymap}
                    className="flex items-center gap-3 rounded-[1rem_0rem_1rem_0rem] bg-port-blue px-6 py-3 text-white transition-opacity hover:opacity-90"
                >
                    <ArrowRightIcon className="h-8 w-8" />
                    <span className="text-xl font-semibold">Explore the Map</span>
                </button>

                <p className="mt-5 text-sm leading-relaxed text-port-text">
                    The Port created the Index to visualize and better understand differences in social and environmental factors impacting quality of life in King County. By bringing important data together in one place, the Index broadly informs and help the Port make fair decisions about providing resources, shaping policies, and supporting community planning and advocacy.
                </p>

                <p className="mt-3 text-sm leading-relaxed text-port-text">
                    We use the Equity Index to understand our impact on specific aspects of quality of life: Economy, Livability, Accessibility, and Environment.  
                </p>

                <p className="mt-4 text-sm italic text-port-blue font-semibold">
                    Select an indicator to learn more and open the Equity Index.
                </p>

                {/* Indicator Icon Buttons */}
                <div className="mt-5 flex flex-wrap gap-3 items-center justify-around">
                    {indicators.map(({ icon: Icon, label, description }, i) => { 
                        const tooltipId = `category-tooltip-${i}`;

                        return (
                            <div key={i} className="relative group flex flex-col items-center">
                            <button
                                key={label}
                                type="button"
                                aria-label={label}
                                onClick={() => {
                                    toggleOverlayLayer(indicators[i].id, `${indicators[i].label} Index`); handleCloseStorymap();
                                }}
                                className="flex h-12 w-14 items-center justify-center bg-white border-2 border-port-blue rounded-[1rem_0rem_1rem_0rem] shadow-lg transition-transform hover:scale-105 md:h-14 md:w-16"
                            >
                                <img src={Icon} className="h-12 w-12 md:h-12 md:w-12" />
                            </button>

                            <div
                                id={tooltipId}
                                role="tooltip"
                                className="pointer-events-none absolute bottom-full mb-9 w-80 opacity-0 scale-95 transition-all duration-200
                                        group-hover:opacity-100 group-hover:scale-100
                                        group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:scale-100"
                            >
                                <div
                                className="relative rounded-xl bg-white py-2 px-4 shadow-xl border border-port-blue"
                                >
                                <p className="text-sm text-gray-700">
                                    <span className="font-bold">{label}</span> {description}
                                </p>
                                <p className="mt-2 text-sm font-semibold text-port-blue">
                                    Learn more about {label}.
                                </p>

                                <div className="absolute -mt-1.5 left-1/2 top-full -translate-x-1/2 flex flex-col items-center">
                                    <div
                                        className="h-3 w-3 rounded-full border-2 bg-white border-port-blue"
                                        />
                                    <div
                                        className="w-[2px] h-6 bg-port-blue"
                                        />
                                    <div
                                        className="h-3 w-3 rounded-full border-2 bg-white border-port-blue"
                                    />
                                </div>
                                </div>
                            </div>
                            </div>
                        );
                    })}
                </div>
                <p className='mt-5 text-sm leading-relaxed text-port-text'>
                    Learn more about the Index <a className='underline text-[#0000FF] visited:text-[#800080] active:text-[#FF0000]' href='https://www.portseattle.org/equityindex'>here</a>.
                </p>
            </div>

            {/* SKPCF Card */}
            <div className="rounded-[3rem_0rem_3rem_0rem] bg-[#fbfbfb] p-8 shadow-lg">
                <div className="flex items-start border-b-2 border-black pb-4 mb-4">
                    <h2 className="text-2xl font-bold md:text-3xl">
                        Community Impact Stories
                    </h2>
                </div>

                {/* Explore Our Stories Button */}
                <button
                    type="button"
                    onClick={() => {
                        handleRestoreDefaultView();
                        navigate("/storymap");
                    }}
                    className="flex items-center gap-3 rounded-[1rem_0rem_1rem_0rem] bg-port-blue px-6 py-3 text-white transition-opacity hover:opacity-90"
                >
                    <ArrowRightIcon className="h-8 w-8" />
                    <span className="text-xl font-semibold">Explore Our Stories</span>
                </button>

                <p className="mt-5 text-sm leading-relaxed text-port-text">
                    By investing in local communities, the Port of Seattle creates shared economic opportunities, strengthens trust, and supports the people and neighborhoods most affected by Port-related activities.
                </p>

                <p className="mt-3 text-sm leading-relaxed text-port-text">
                    We strive to build partnerships that honor and strengthen communities, and these are our stories.  
                </p>

                <p className="mt-4 text-sm italic text-port-blue font-semibold">
                    Explore below to learn about some of our programs and partners.
                </p>

                {/* Partner Image Buttons */}
                <div className="mt-5 flex flex-wrap gap-5 justify-around">
                    {partners.map(({ src, alt, color, title, shortDescription }, i) => {
                    const tooltipId = `partner-tooltip-${i}`;

                    return (
                        <div key={i} className="relative group flex flex-col items-center">
                        <button
                            type="button"
                            aria-label={`View partner ${i + 1}: ${title}`}
                            aria-describedby={tooltipId}
                            onClick={() => {
                                handleRestoreDefaultView();

                                const params = new URLSearchParams({
                                    partnerIndex: String(i),
                                    scrollTo: "partners",
                                });

                                navigate(`/storymap?${params.toString()}`);
                            }}
                            className="relative h-20 w-20 overflow-hidden rounded-full transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 md:h-24 md:w-24"
                            style={{ boxShadow: `0 0 0 4px ${color}` }}
                        >
                            <img src={src} alt={alt} className="h-full w-full object-contain p-2" />
                        </button>

                        <div
                            id={tooltipId}
                            role="tooltip"
                            className="pointer-events-none absolute bottom-full mb-10 w-80 opacity-0 scale-95 transition-all duration-200
                                    group-hover:opacity-100 group-hover:scale-100
                                    group-has-[:focus-visible]:opacity-100 group-has-[:focus-visible]:scale-100"
                        >
                            <div
                            className="relative rounded-xl bg-white py-2 px-4 shadow-xl border"
                            style={{ borderColor: color }}
                            >
                            <p className="text-sm text-gray-700">
                                <span className="font-bold">{title}</span> {shortDescription}
                            </p>
                            <p className="mt-2 text-sm font-semibold" style={{ color }}>
                                Learn more about {title}.
                            </p>

                            <div className="absolute -mt-1.5 left-1/2 top-full -translate-x-1/2 flex flex-col items-center">
                                <div
                                    className="h-3 w-3 rounded-full border-2 bg-white"
                                    style={{ borderColor: color }}
                                    />
                                <div
                                    className="w-[2px] h-6"
                                    style={{ backgroundColor: color }}
                                    />
                                <div
                                    className="h-3 w-3 rounded-full border-2 bg-white"
                                    style={{ borderColor: color }}
                                />
                            </div>
                            </div>
                        </div>
                        </div>
                    );
                    })}
                </div>
            </div>
        </div>
        {/* Logos */}
        <div className='flex justify-end items-end gap-8 mx-auto px-6 pb-4 md:px-12 lg:px-48 bg-transparent'>
            <img src={portLogo} alt='Port of Seattle logo' className='w-auto h-12'/>
            <img src={caiLogo} alt='Community Attributes logo' className='w-auto h-12'/>
        </div>
    </section>
  );
}

export default Intro;